<script setup>
// Gestão → Minhas avaliações: o atendente vê as próprias notas e o feedback do
// analista, para melhorar (quando a empresa liga a opção). Só leitura.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AvaliacaoDetalhe from './AvaliacaoDetalhe.vue';
import { createGestaoApi, nota, quando } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const api = computed(() => createGestaoApi(props.accountId));

const dados = ref(null);
const itens = ref([]);
const carregando = ref(true);
const erro = ref(false);
const escolhida = ref(null);
const detalhe = ref(null);
const carregandoDetalhe = ref(false);

const carrega = async (mais = false) => {
  carregando.value = true;
  erro.value = false;
  try {
    const { data } = await api.value.minhas(mais ? dados.value.pagina + 1 : 1);
    dados.value = data;
    itens.value = mais ? [...itens.value, ...data.itens] : data.itens;
  } catch {
    erro.value = true;
  } finally {
    carregando.value = false;
  }
};
onMounted(() => carrega());

const abre = async id => {
  escolhida.value = id;
  carregandoDetalhe.value = true;
  try {
    const { data } = await api.value.minha(id);
    if (escolhida.value === id) detalhe.value = data;
  } catch {
    detalhe.value = null;
  } finally {
    carregandoDetalhe.value = false;
  }
};

const baixa = valor => valor !== null && valor !== undefined && valor < 6;
const temMais = computed(() => dados.value && itens.value.length < dados.value.total);
</script>

<template>
  <div class="flex flex-col gap-5">
    <div v-if="carregando && !dados" class="flex justify-center py-10"><Spinner /></div>
    <div v-else-if="erro && !dados" class="flex items-center gap-3 text-sm text-n-ruby-11">
      {{ t('NEUXS_GESTAO.ERRO') }}
      <Button size="sm" variant="faded" :label="t('NEUXS_GESTAO.TENTAR')" @click="carrega()" />
    </div>

    <template v-else-if="dados">
      <div class="grid grid-cols-2 gap-3 md:max-w-lg">
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MINHAS.MEDIA') }}</span>
          <strong
            class="block text-heading-1 tabular-nums"
            :class="baixa(dados.nota_media) ? 'text-n-ruby-11' : 'text-n-slate-12'"
          >
            {{ nota(dados.nota_media) }}
          </strong>
        </div>
        <div class="px-4 py-3 border rounded-xl border-n-weak">
          <span class="text-xs text-n-slate-11">{{ t('NEUXS_GESTAO.MINHAS.AVALIADAS') }}</span>
          <strong class="block text-heading-1 text-n-slate-12 tabular-nums">{{ dados.avaliadas }}</strong>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] items-start">
        <section class="flex flex-col gap-2">
          <p v-if="!itens.length" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.VAZIO') }}</p>
          <ul v-else class="flex flex-col gap-1.5 p-0 m-0 list-none">
            <li v-for="item in itens" :key="item.id">
              <button
                type="button"
                class="grid w-full grid-cols-[1fr_auto] gap-x-3 gap-y-0.5 px-3 py-2 text-start rounded-lg border"
                :class="escolhida === item.id ? 'border-n-blue-9 bg-n-alpha-1' : 'border-transparent bg-n-alpha-1 hover:bg-n-alpha-2'"
                @click="abre(item.id)"
              >
                <span class="text-sm truncate text-n-slate-12">{{ item.cliente || '—' }}</span>
                <span
                  class="row-span-2 self-center px-2 py-0.5 rounded-md text-sm tabular-nums"
                  :class="baixa(item.nota) ? 'bg-n-ruby-3 text-n-ruby-11' : 'bg-n-blue-3 text-n-blue-11'"
                >
                  {{ nota(item.nota) }}
                </span>
                <span class="text-xs truncate text-n-slate-11">
                  {{ quando(item.avaliada_em) }}
                  <template v-if="item.assunto"> · {{ item.assunto }}</template>
                </span>
              </button>
            </li>
          </ul>
          <Button
            v-if="temMais"
            size="sm"
            variant="faded"
            color="slate"
            :label="t('NEUXS_GESTAO.MAIS')"
            :is-loading="carregando"
            @click="carrega(true)"
          />
        </section>

        <section class="p-4 border rounded-xl border-n-weak">
          <div v-if="carregandoDetalhe" class="flex justify-center py-10"><Spinner /></div>
          <p v-else-if="!detalhe" class="m-0 text-sm text-n-slate-11">{{ t('NEUXS_GESTAO.ESCOLHA') }}</p>
          <AvaliacaoDetalhe v-else :avaliacao="detalhe" :account-id="accountId" />
        </section>
      </div>
    </template>
  </div>
</template>
